import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Card,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { RouteComponentProps } from 'react-router-dom';
import { Api } from '../../data_sources/api/api';
import { IDocumentShareView } from '../../models/DocumentShare';
import { LoadStatus } from '../common/types';
import { getErrorMessage } from '../../utils';
import DocumentExportPanel from './DocumentExportPanel';
import { formatReviewDate } from './documentShareFormat';
import { useDocumentShareStyles, DOC_ICON_FG } from './documentShareStyles';

interface DocumentSharePublicRouteParams {
    region: string;
    token: string;
}

interface DocumentSharePublicViewProps extends RouteComponentProps<DocumentSharePublicRouteParams> {}

/**
 * Public view for a fully-public compliance document share.
 *
 * Accessed via {@code /:region/document-public/:token} — the token in the URL is
 * the sole credential; no email address or login is required.
 *
 * This is a read-only view: the server always sets {@code signOff = HIDDEN} for public
 * links, so no acknowledgement form is ever shown.
 */
const DocumentSharePublicView: React.FC<DocumentSharePublicViewProps> = (props) => {
    const classes = useDocumentShareStyles();
    const { region, token } = props.match.params;

    const [view, setView] = useState<IDocumentShareView | null>(null);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        let active = true;
        async function load() {
            try {
                const api = new Api(region, '');
                const result = await api.getPublicDocumentShare(token);
                if (!active) return;
                setView(result);
                setLoadStatus('loaded');
            } catch (e: unknown) {
                if (!active) return;
                setErrorMessage(getErrorMessage(e) || 'Unable to load the document.');
                setLoadStatus('load_error');
            }
        }
        load();
        return () => {
            active = false;
        };
    }, [region, token]);

    if (loadStatus === 'loading') {
        return (
            <Container className={classes.center} maxWidth="sm">
                <CircularProgress />
                <Typography variant="h6" style={{ marginTop: 16 }}>
                    Loading document…
                </Typography>
            </Container>
        );
    }

    if (loadStatus === 'load_error' || !view) {
        return (
            <Container className={classes.page} maxWidth="md">
                <Grid container className={classes.wrap}>
                    <div className={classes.stack}>
                        <Card variant="outlined" className={classes.hero}>
                            <Alert severity="error">{errorMessage}</Alert>
                        </Card>
                    </div>
                </Grid>
            </Container>
        );
    }

    const attachments = view.attachments ?? [];

    return (
        <Container className={classes.page} maxWidth="md">
            <Grid container className={classes.wrap}>
                <div className={classes.stack}>
                    {/* Hero */}
                    <Card variant="outlined" className={classes.hero}>
                        <div className={classes.heroRow}>
                            <Avatar className={classes.heroAvatar} variant="rounded">
                                <DescriptionOutlinedIcon style={{ color: DOC_ICON_FG }} />
                            </Avatar>
                            <div className={classes.heroBody}>
                                <Typography className={classes.eyebrow}>
                                    {view.documentType || 'Document'}
                                </Typography>
                                <h1 className={classes.heroTitle}>{view.documentName}</h1>
                                {view.versionNumber && (
                                    <div className={classes.metaRow}>
                                        <Chip
                                            size="small"
                                            className={classes.versionChip}
                                            label={`v${view.versionNumber}`}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Message */}
                    {view.message && (
                        <Card variant="outlined" className={classes.sectionCard}>
                            <div className={classes.panelHeaderBar}>
                                <Typography className={classes.panelHeading}>
                                    Message
                                </Typography>
                            </div>
                            <div className={classes.panelBody}>
                                <Typography className={classes.message}>
                                    {view.message}
                                </Typography>
                            </div>
                        </Card>
                    )}

                    {/* Details */}
                    {(view.versionDescription ||
                        view.versionNotes ||
                        view.reviewDate) && (
                        <Card variant="outlined" className={classes.sectionCard}>
                            <div className={classes.panelHeaderBar}>
                                <Typography className={classes.panelHeading}>
                                    Details
                                </Typography>
                            </div>
                            <div className={classes.panelBody}>
                                {view.versionDescription && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Description
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {view.versionDescription}
                                        </Typography>
                                    </div>
                                )}
                                {view.versionNotes && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Version notes
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {view.versionNotes}
                                        </Typography>
                                    </div>
                                )}
                                {view.reviewDate && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Review date
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {formatReviewDate(view.reviewDate)}
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    {/* Attachments */}
                    <Card variant="outlined" className={classes.sectionCard}>
                        <div className={classes.panelHeaderBar}>
                            <Typography className={classes.panelHeading}>
                                Attachments
                            </Typography>
                        </div>
                        <div className={classes.panelBody}>
                            <DocumentExportPanel
                                view={view}
                                attachments={attachments}
                                showDownload={false}
                                urlFor={(id) =>
                                    new Api(region, '').publicDocumentShareAttachmentUrl(
                                        token,
                                        id
                                    )
                                }
                            />
                        </div>
                    </Card>
                </div>
            </Grid>
        </Container>
    );
};

export default DocumentSharePublicView;
